import React from "react";
import { Leaf } from "lucide-react";
import { useTranslations } from "next-intl";
import { energyLabels } from "@/db/definitions";
import { Slider } from "@/components/ui/slider";
import FilterWrapper from "./FilterWrapper";

type Props = {
  /** Minimum accepted label, or undefined for any label (including unknown). */
  value: string | undefined;
  setValue: (value: string | undefined) => void;
};

// EU energy label colours, best (A++++) to worst (G).
const LABEL_COLORS: Record<(typeof energyLabels)[number], string> = {
  "A++++": "#00703c",
  "A+++": "#00813f",
  "A++": "#009043",
  "A+": "#009c48",
  A: "#00a651",
  B: "#50b848",
  C: "#bed630",
  D: "#ffd500",
  E: "#fdb913",
  F: "#f37021",
  G: "#ed1c24",
};

// Light label colours need dark text.
const DARK_TEXT: string[] = ["C", "D", "E"];

// One extra step after G means "any label", which also keeps listings
// that never published one.
const ANY_STEP = energyLabels.length;

/** "X or better" picker: slide right to accept less efficient homes. */
const FilterEnergyLabel: React.FC<Props> = ({ value, setValue }) => {
  const t = useTranslations("search");
  const tL = useTranslations("listing");
  const index = value
    ? energyLabels.indexOf(value as (typeof energyLabels)[number])
    : -1;
  const step = index === -1 ? ANY_STEP : index;
  const label = step === ANY_STEP ? undefined : energyLabels[step];
  const readout = label
    ? t("energy-label-or-better", { label })
    : t("energy.any");

  return (
    <FilterWrapper
      icon={<Leaf />}
      title={tL("attribute_title.ENERGY_LABEL")}
      summary={
        label ? (
          <span
            className="rounded-md px-1.5 py-0.5 font-semibold text-white"
            style={{
              backgroundColor: LABEL_COLORS[label],
              color: DARK_TEXT.includes(label) ? "#111827" : "#ffffff",
            }}
          >
            {readout}
          </span>
        ) : (
          readout
        )
      }
    >
      <div className="flex flex-col gap-2">
        <Slider
          min={0}
          max={ANY_STEP}
          step={1}
          value={[step]}
          onValueChange={([next]) =>
            setValue(next === ANY_STEP ? undefined : energyLabels[next])
          }
          thumbLabels={[t("minimum-energy-label")]}
          thumbValueTexts={[readout]}
          className="py-1.5"
          trackClassName="h-2.5 bg-transparent"
          rangeClassName="bg-transparent"
          thumbClassName="flex h-7 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-zinc-400 p-0 shadow-md ring-1 ring-black/15"
          thumbContent={[
            <span
              key="thumb"
              className="flex size-full items-center justify-center text-[11px] font-bold leading-none"
              style={
                label
                  ? {
                      backgroundColor: LABEL_COLORS[label],
                      color: DARK_TEXT.includes(label) ? "#111827" : "#ffffff",
                    }
                  : { color: "#ffffff" }
              }
            >
              {label}
            </span>,
          ]}
          track={
            // The whole EU scale stays visible; accepted labels are solid,
            // the rest are faded.
            <span className="absolute inset-0 flex">
              {[...energyLabels, null].map((item, i) => (
                <span
                  key={item ?? "any"}
                  className={`h-full flex-1 transition-opacity ${
                    item ? "" : "bg-zinc-400"
                  } ${i > step ? "opacity-25" : ""}`}
                  style={
                    item ? { backgroundColor: LABEL_COLORS[item] } : undefined
                  }
                />
              ))}
            </span>
          }
        />
        <div
          aria-hidden
          className="flex justify-between text-[11px] font-medium text-muted-foreground"
        >
          <span>{t("energy.best")}</span>
          <span>{t("energy.any")}</span>
        </div>
      </div>
    </FilterWrapper>
  );
};

export default FilterEnergyLabel;
